// TUT-444 — tipos dos prazos: a fila, os tipos do escritório, responsáveis e criação.
import type { EcoDePaginacao, ProcessStatus } from "../types";

export type DeadlineStatusKey =
  | "pending"
  | "overdue"
  | "completed"
  | "cancelled";
export type DeadlineUpdateStatusKey = "pending" | "completed" | "cancelled";
export interface DeadlineItemStatus extends ProcessStatus {
  key: DeadlineStatusKey;
}
export interface DeadlineItem {
  id: string;
  processId: string;
  processNumber: string;
  claimant: string;
  title: string;
  type: string;
  dueAt: string;
  status: DeadlineItemStatus;
  overdue: boolean;
  ownerName: string;
  ownerInitials: string;
  assignedUserId: string | null;
  /**
   * TUT-495 — a regra com que o vencimento foi contado em dias úteis. Nula
   * quando a data final foi informada (e em todo prazo anterior à fatia, que
   * não é recalculado). A API sempre manda (`null` ou a regra); é opcional
   * aqui para as sementes do demo e as fixtures antigas não precisarem dizer
   * `null` em cada linha.
   */
  dueRule?: DeadlineDueRule | null;
  /**
   * TUT-246 — as colunas `ASSUNTO` e `CLIENTE` da tabela "Atribuídos a mim"
   * (Visão Geral Simples). As duas vêm de `processes`, que a lista de prazos
   * já une por join; o cliente vem como ID, e a tela resolve o nome com o
   * catálogo de organizações que ela já carrega.
   */
  subject: string;
  clientOrganizationId: string | null;
}
export interface AssignableMember {
  userId: string;
  name: string;
}
export interface AssignableMembersResponse {
  items: AssignableMember[];
}
export interface UpdateDeadlineInput {
  status: DeadlineUpdateStatusKey;
}
/**
 * Tipo de prazo do catálogo do escritório (TUT-124), semeado com os tipos
 * reais do legado. O prazo grava o RÓTULO como retrato — desativar um tipo
 * não reescreve prazo nenhum.
 */
export interface DeadlineTypeSummary {
  id: string;
  label: string;
  active: boolean;
}
export interface DeadlineTypeListResponse {
  items: DeadlineTypeSummary[];
}
/** A regra gravada com o prazo contado em dias úteis (TUT-495). */
export interface DeadlineDueRule {
  /** Dia civil de início, `AAAA-MM-DD` (o dia do começo não conta). */
  startDate: string;
  businessDays: number;
  /** Versão do gerador de calendário com que o prazo foi contado. */
  calendarVersion: string;
  /** Os feriados do escritório que mudaram a contagem. */
  officeHolidays: { data: string; descricao: string }[];
}
export interface CreateDeadlineInput {
  title: string;
  /** Rótulo de um tipo ATIVO do catálogo do escritório (TUT-124). */
  type: string;
  /** A data final informada — OU `count` (TUT-495); exatamente um dos dois. */
  dueAt?: string;
  /** TUT-495 — "N dias úteis a partir de [data]": o servidor calcula o vencimento. */
  count?: { startDate: string; businessDays: number };
  owner: string;
  assignedUserId?: string;
}
/** O resultado da contagem em dias úteis, sem gravar nada (TUT-495). */
export interface BusinessDaysPreview {
  /** Dia do vencimento, `AAAA-MM-DD`. */
  dueDate: string;
  /** O instante do vencimento: fim do dia útil, no fuso do escritório. */
  dueAt: string;
  rule: DeadlineDueRule;
}
export const OFFICE_HOLIDAY_KINDS = [
  "FERIADO_ESTADUAL",
  "FERIADO_MUNICIPAL",
  "SUSPENSAO",
  "PONTO_FACULTATIVO",
] as const;
export type OfficeHolidayKind = (typeof OFFICE_HOLIDAY_KINDS)[number];
/** Feriado da lista única do escritório (TUT-495). */
export interface OfficeHoliday {
  id: string;
  /** Dia civil, `AAAA-MM-DD`. */
  date: string;
  description: string;
  kind: OfficeHolidayKind;
}
export interface OfficeHolidayListResponse {
  items: OfficeHoliday[];
}
export interface CreateOfficeHolidayInput {
  date: string;
  description: string;
  kind: OfficeHolidayKind;
}
export interface DeadlineListResponse extends EcoDePaginacao {
  items: DeadlineItem[];
  total: number;
  summary: {
    windowDays: number;
    total: number;
    pending: number;
    overdue: number;
    dueSoon: number;
    completed: number;
  };
}
